import { CustomAnalyticsEvent, ExternalPixelEvent } from '@betterfunnel/types';
import {
  arrayRandomElement,
  arrayRandomElements,
  camelCaseToTitle,
  capitalize,
  chunk,
  distinct,
  formatDuration,
  getRandomElementWithSeed,
  groupBy,
} from '@betterfunnel/utils';
import { DocumentData, FirestoreDataConverter } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { RefObject } from 'react';
import { twMerge } from 'tailwind-merge';
import { storage } from '../Firebase';
import { AnalyticsRange } from '../contexts/DashboardContext';

// Re-export the imported utils
export {
  arrayRandomElement,
  arrayRandomElements,
  camelCaseToTitle,
  capitalize,
  chunk,
  distinct,
  formatDuration,
  getRandomElementWithSeed,
  groupBy,
};

// makes sure a function only invoes once,
export function concurrentSafeExecution<
  T extends (...args: any[]) => Promise<any>,
>(asyncFn: T): T {
  let promise: Promise<T> | null = null;

  const execute = async (...args: any[]): Promise<T> => {
    if (!promise) {
      const _promise = asyncFn(...args);
      promise = _promise;
      if (_promise?.finally) {
        _promise.finally(() => (promise = null));
      } else {
        promise = null;
      }

      return _promise;
    }
    return promise;
  };

  return execute as any;
}

export const emptyConverter = <
  T extends DocumentData,
>(): FirestoreDataConverter<T, T> => {
  return {
    toFirestore: (v) => v as any,
    fromFirestore: (v) => v.data() as any,
  };
};

export function classNames(...classes: any[]) {
  return twMerge(...classes.filter(Boolean));
}

export async function uploadFile(file: File, path: string) {
  const res = await uploadBytes(ref(storage, path), file);

  return getDownloadURL(res.ref);
}

// export const PREVIEW_ROOT = 'https://preview_.betterfunnel.page';
// export const PREVIEW_ROOT = 'http://preview_.localhost:4000';
export const PREVIEW_ROOT = 'https://betterfunnel.site';

export function buildPreviewUrl(
  domain: string,
  accountId: string,
  dynamicPageId: string,
  websiteId: string,
  token: string,
  editable: boolean,
) {
  return `${PREVIEW_ROOT}?site=${domain}&websiteId=${websiteId}&accountId=${accountId}&campaignId=${dynamicPageId}&token=${token}${
    editable ? `&editable=${editable}` : ''
  }`;
}

export const STEPPER_PREVIEW_URL = `${PREVIEW_ROOT}/stepper`;

export function sendMessageToIframe(
  message: any,
  ref?: RefObject<HTMLIFrameElement | null>,
) {
  const cleanMessage = JSON.stringify(message);

  if (ref?.current) {
    ref.current.contentWindow?.postMessage(cleanMessage, PREVIEW_ROOT);
    return true;
  }

  const iframes = document.getElementsByClassName('iframe-website-preview');

  for (const iframeElem of iframes) {
    (iframeElem as HTMLIFrameElement).contentWindow?.postMessage(
      cleanMessage,
      PREVIEW_ROOT,
    );
  }

  return true;
}

export function sendMessageToIframeByWebsiteId(
  message: any,
  websiteId: string,
) {
  const cleanMessage = JSON.stringify(message);

  const iframes = document.getElementsByClassName('iframe-website-preview');

  for (const iframeElem of iframes) {
    const iframeWebsiteId =
      iframeElem.attributes.getNamedItem('data-website-id')?.value;
    if (!iframeWebsiteId) continue;
    if (iframeWebsiteId !== websiteId) continue;
    (iframeElem as HTMLIFrameElement).contentWindow?.postMessage(
      cleanMessage,
      PREVIEW_ROOT,
    );
  }

  return true;
}

export type WithId<T> = { id: string } & T;

function dateToString(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function analyticsRangeToDates(analyticsRange: AnalyticsRange): {
  startDate: string;
  endDate: string;
} {
  if (!analyticsRange) {
    throw new Error('analyticsRange is required');
  }

  if (typeof analyticsRange === 'string') {
    switch (analyticsRange) {
      case 'today': {
        const startDate = new Date();
        const endDate = new Date();
        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      case 'yesterday': {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 1);
        const endDate = new Date();
        endDate.setDate(endDate.getDate() - 1);

        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      case 'last7Days': {
        const endDate = new Date();
        const startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);
        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      case 'last30Days': {
        const endDate = new Date();
        const startDate = new Date(
          endDate.getTime() - 30 * 24 * 60 * 60 * 1000,
        );
        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      case 'currentMonth': {
        const startDate = new Date();
        startDate.setDate(1);
        const endDate = new Date();
        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      default:
        throw new Error('unsupported date range');
    }
  }

  const startDate = new Date(analyticsRange.startDate || '');
  const endDate = new Date(analyticsRange.endDate || '');

  return {
    startDate: dateToString(startDate),
    endDate: dateToString(endDate),
  };
}

export function isJwtExpired(token: string): boolean {
  if (!token) return true;

  // Split the token into its parts
  const tokenParts = token.split('.');
  if (tokenParts.length !== 3) return true; // Invalid token format

  try {
    // Decode the payload (second part of the token)
    const base64Url = tokenParts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );

    const { exp } = JSON.parse(jsonPayload);

    // Check if the token has expired
    if (!exp) return true; // No exp claim in token
    const now = Math.floor(Date.now() / 1000); // Current time in seconds

    return now >= exp; // Token is expired if current time is greater than or equal to exp
  } catch (_) {
    return true; // Error decoding token
  }
}

export type AnalyticsAttributes = {
  visitors: number;
  conversions: number;
  allConversions: number;
  buttonClicks: number;
  uniqueButtonClicks: number;
  formSubmissions: number;
  cr: number;
};

export type AnalyticsAttributesWithCustomEvents = AnalyticsAttributes & {
  [key: string]: number;
};

export function getAnalyticsAttributes(
  key: string,
  analyticsObj?: Record<string, any>,
): AnalyticsAttributes {
  const visitors = analyticsObj?.uniqueVisitors?.[key] ?? 0;
  const conversions = analyticsObj?.uniqueConversions?.[key] ?? 0;
  const allConversions = analyticsObj?.uniqueConversions?.['totalCustom'] ?? 0;
  const buttonClicks = analyticsObj?.uniqueButtonClicks?.[key] ?? 0;
  const uniqueButtonClicks =
    analyticsObj?.uniqueButtonClicks?.['uniqueCustom'] ?? 0;
  const formSubmissions = analyticsObj?.uniqueFormSubmissions?.[key] ?? 0;
  const cr = visitors ? Math.ceil((conversions / visitors) * 100) : 0;

  return {
    visitors,
    conversions,
    allConversions,
    buttonClicks,
    uniqueButtonClicks,
    formSubmissions,
    cr,
  };
}

export function getAnalyticsAttributesWithEvents(
  key: string,
  analyticsObj?: Record<string, any>,
  customEvents?: (ExternalPixelEvent | CustomAnalyticsEvent)[],
): AnalyticsAttributesWithCustomEvents {
  const attrs = getAnalyticsAttributes(key, analyticsObj);

  return Object.assign(
    {},
    attrs,
    customEvents?.reduce(
      (acc, event) => {
        acc[event.id] = analyticsObj?.[event.id]?.[key] ?? 0;
        return acc;
      },
      {} as Record<string, number>,
    ),
  );
}
